import { Collapse, CollapseProps } from 'antd';
import classNames from 'classnames';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch } from 'app/config/storeConfig/hooks';
import { Box } from 'entities/Box';
import { ReactComponent as FilledCollapseArrow } from 'shared/assets/icons/FilledCollapseArrow.svg';
import { PIXELS_PER_METER } from '../../../model/constants/mapScale';
import { warehouseMapConstructorActions } from '../../../model/slice/warehouseMapConstructorSlice';
import { PickedBoxParams, ShapeType } from '../../../model/types';
import { Point } from '../../../utils/helpers/coordinates';
import { PickerBoxGroup, usePickerBoxes } from '../../../utils/hooks/usePickerBoxes';

interface BoxesListProps {
  warehouseId: string | undefined;
  floorId: string;
  /** Returns the world-space, snap-aligned point at which a click-placed box should land. */
  getDropPoint: () => Point;
  /** True when the warehouse has at least one configured size code. Used by the empty-state branch. */
  hasAvailableSizeCodes: boolean;
}

interface BoxCellProps {
  box: Box;
  onPick: (box: Box) => void;
}

const PREVIEW_CELL_SIZE = 14;

const BoxCell: FC<BoxCellProps> = memo(({ box, onPick }) => {
  const { boxId, width, length, name } = box;
  return (
    <div
      key={boxId}
      style={{
        width: Math.max(PREVIEW_CELL_SIZE * width, 80),
        height: Math.max(PREVIEW_CELL_SIZE * length, 52),
      }}
      className="flex flex-col justify-center items-center bg-secondaryAccent border border-primaryLight rounded-lg px-1 overflow-hidden cursor-pointer"
      title={`${name} (${width}x${length})`}
      onClick={() => {
        onPick(box);
      }}
    >
      <div className="text-[11px] leading-tight text-center w-full truncate">{name}</div>
      <div className="text-[11px] leading-tight">
        {width}x{length}
      </div>
    </div>
  );
});

export const BoxesList: FC<BoxesListProps> = memo((props) => {
  const { warehouseId, floorId, getDropPoint, hasAvailableSizeCodes } = props;

  const { t } = useAppTranslation('warehouse-management');
  const dispatch = useAppDispatch();
  const { groupsBySizeCode } = usePickerBoxes({ warehouseId });

  const pickBox = useCallback(
    ({ id, width, length, label, sizeCodeId }: PickedBoxParams): void => {
      const drop = getDropPoint();
      dispatch(
        warehouseMapConstructorActions.appendShape({
          id,
          floorId,
          type: ShapeType.RECTANGLE,
          fill: '#F3F6F9',
          stroke: '#788593',
          width: width * PIXELS_PER_METER,
          height: length * PIXELS_PER_METER,
          x: drop.x,
          y: drop.y,
          strokeWidth: 1,
          cornerRadius: 5,
          scaleX: 1,
          scaleY: 1,
          label,
          sizeCodeId,
        }),
      );
    },
    [dispatch, floorId, getDropPoint],
  );

  const handlePickBox = useCallback(
    (box: Box): void => {
      pickBox({
        width: box.width,
        length: box.length,
        label: box.name,
        id: box.boxId,
        sizeCodeId: box.sizeCodeId,
      });
    },
    [pickBox],
  );

  const items = useMemo(
    () =>
      groupsBySizeCode.map((group: PickerBoxGroup) => ({
        key: group.sizeCodeId,
        label: (
          <div className="flex items-center justify-between gap-2 w-full">
            <span className="truncate">
              {t('Box ({{sizeGroup}}) - {{square}} m²', { sizeGroup: group.sizeGroup, square: group.square })}
            </span>
            <span className="text-xs text-primaryLight shrink-0">{t('{{count}} available', { count: group.boxes.length })}</span>
          </div>
        ),
        children: (
          <div className="flex flex-wrap gap-3">
            {group.boxes.map((box) => (
              <BoxCell key={box.boxId} box={box} onPick={handlePickBox} />
            ))}
          </div>
        ),
      })),
    [groupsBySizeCode, handlePickBox, t],
  );

  const defaultActiveKey = useMemo(() => (groupsBySizeCode[0] ? [groupsBySizeCode[0].sizeCodeId] : []), [groupsBySizeCode]);

  const renderExpandIcon = useCallback<NonNullable<CollapseProps['expandIcon']>>(
    (panelProps) =>
      panelProps.collapsible !== 'disabled' ? (
        <FilledCollapseArrow
          className={classNames('ease-linear duration-200', {
            'rotate-90 fill-accent': panelProps.isActive,
            'fill-primaryLight': !panelProps.isActive,
          })}
        />
      ) : undefined,
    [],
  );

  if (!hasAvailableSizeCodes) {
    return (
      <div className="p-4 text-center text-sm text-primaryLight border border-dashed border-secondaryAccent rounded-lg">
        {t('No size codes configured for this warehouse. Create size codes to start placing boxes.')}
      </div>
    );
  }

  if (groupsBySizeCode.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-primaryLight border border-dashed border-secondaryAccent rounded-lg">
        {t('All boxes are placed on the map.')}
      </div>
    );
  }

  return <Collapse bordered={false} ghost defaultActiveKey={defaultActiveKey} items={items} expandIcon={renderExpandIcon} />;
});
